// 宿 QR (2026-09-25, 民泊 PoC Step 1): 宿 QR 経由の配布上限 2 項目の設定カード。
//
//   FACILITY_QR_DAILY_CAP               : 宿 (スポット) ごと・1 日 (日本時間 0 時区切り) の配布上限。0 = 上限なし
//   FACILITY_QR_PER_USER_TEMPLATE_LIMIT : 1 ゲスト × 1 店 (= クーポン) の取得回数上限。0 = 上限なし
//
// 設定画面の本体フォーム (20 項目の一括保存) とは独立した保存ボタンを持つ (= 既存フォームの差分を最小化)。
// api 側の読み取り: FacilityQrService.getDailyCap / getPerUserTemplateLimit。

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { SETTING_FACILITY_QR_DAILY_CAP, SETTING_FACILITY_QR_PER_USER_TEMPLATE_LIMIT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

const DEFAULT_DAILY_CAP = 50;
const DEFAULT_PER_USER_TEMPLATE_LIMIT = 1;
const MAX_VALUE = 99999;

const toInputValue = (raw: unknown, fallback: number) =>
    String(raw != null && raw !== '' && Number.isFinite(Number(raw)) ? Number(raw) : fallback);

const FacilityQrSettingsCard = observer(function FacilityQrSettingsCard() {
    const { settingStore: { getSettingByKey, updateSettingByKey } } = useStore();
    const [dailyCap, setDailyCap] = useState<string>(String(DEFAULT_DAILY_CAP));
    const [perUserLimit, setPerUserLimit] = useState<string>(String(DEFAULT_PER_USER_TEMPLATE_LIMIT));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const [capRes, limitRes]: any[] = await Promise.all([
                flowResult(getSettingByKey(SETTING_FACILITY_QR_DAILY_CAP)),
                flowResult(getSettingByKey(SETTING_FACILITY_QR_PER_USER_TEMPLATE_LIMIT))
            ]);
            setDailyCap(toInputValue(capRes?.data?.value, DEFAULT_DAILY_CAP));
            setPerUserLimit(toInputValue(limitRes?.data?.value, DEFAULT_PER_USER_TEMPLATE_LIMIT));
        })();
    }, [getSettingByKey]);

    const isValid = (v: string) => /^\d+$/.test(v) && Number(v) <= MAX_VALUE;

    const handleSave = async () => {
        if (!isValid(dailyCap) || !isValid(perUserLimit)) {
            toastify('error', `0〜${MAX_VALUE} の整数で入力してください`);
            return;
        }
        setSaving(true);
        try {
            const results: any[] = await Promise.all([
                flowResult(updateSettingByKey({ key: SETTING_FACILITY_QR_DAILY_CAP, value: Number(dailyCap) })),
                flowResult(updateSettingByKey({ key: SETTING_FACILITY_QR_PER_USER_TEMPLATE_LIMIT, value: Number(perUserLimit) }))
            ]);
            if (results.every(r => r?.ok)) {
                toastify('success', '宿 QR の設定を保存しました');
            } else {
                toastify('error', results.find(r => r?.message)?.message || '保存に失敗しました');
            }
        } finally {
            setSaving(false);
        }
    };

    const numberField = (label: string, hint: string, value: string, onChange: (v: string) => void) => (
        <div className='w-full mt-4 flex items-start gap-3'>
            <label className='text-black font-medium text-[15px] min-w-[200px] pt-2'>{label}</label>
            <div>
                <Input
                    className='w-[167px]'
                    type='text'
                    inputMode='numeric'
                    value={value}
                    onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
                />
                <p className='text-xs text-muted-foreground mt-1'>{hint}</p>
            </div>
        </div>
    );

    return (
        <div className='layout-card mt-4'>
            <div className='text-black-2 font-medium text-[15px]'>宿 QR (民泊) の配布上限</div>
            {numberField(
                '宿ごと 1 日の配布上限',
                '1 つの宿 (スポット) で 1 日 (日本時間 0 時区切り) に配る特典の上限。0 = 上限なし',
                dailyCap,
                setDailyCap
            )}
            {numberField(
                '1 人 1 店あたりの回数',
                '同じゲストが同じ店のクーポンを受け取れる回数。0 = 上限なし',
                perUserLimit,
                setPerUserLimit
            )}
            <div className='mt-4'>
                <Button type='button' variant='active' onClick={handleSave} disabled={saving}>
                    宿 QR の設定を保存
                </Button>
            </div>
        </div>
    );
});

export default FacilityQrSettingsCard;
